import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import { SettingsProvider } from './components/SettingsContext.tsx';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import { initializeAnalytics } from './lib/analytics.ts';
import './index.css';

window.onerror = (msg, src, line, col, err) => {
  console.error('[Global Error]', { msg, src, line, col, err });
};

window.onunhandledrejection = (event) => {
  console.error('[Unhandled Promise]', event.reason);
};

// Observe Consent Mode before any product lifecycle event can be emitted.
initializeAnalytics();

const APP_VERSION: string = (import.meta.env.VITE_APP_VERSION as string) || 'dev';
const LAST_VERSION_KEY = 'sbarazzino:lastVersion';
const FORCE_RELOAD_FLAG = 'sbarazzino:forceReload';

function shouldForceReload(previousVersion: string | null): boolean {
  if (!previousVersion || previousVersion === APP_VERSION) return false;
  return sessionStorage.getItem(FORCE_RELOAD_FLAG) !== APP_VERSION;
}

function forceReload(targetVersion?: string): void {
  const versionToUse = targetVersion || APP_VERSION;
  try {
    sessionStorage.setItem(FORCE_RELOAD_FLAG, versionToUse);
  } catch {
    // Storage can be unavailable in hardened/private browser modes.
  }

  const performRedirect = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('v', versionToUse);
    window.location.replace(url.toString());
  };

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations()
      .then(registrations => Promise.all(registrations.map(registration => registration.unregister())))
      .catch(error => console.error('[Sbarazzino] Failed to unregister old service workers:', error))
      .finally(performRedirect);
  } else {
    performRedirect();
  }
}

(function versionGuard() {
  const previous = (() => {
    try {
      return localStorage.getItem(LAST_VERSION_KEY);
    } catch {
      return null;
    }
  })();

  if (shouldForceReload(previous)) {
    forceReload();
    return;
  }

  try {
    localStorage.setItem(LAST_VERSION_KEY, APP_VERSION);
  } catch {
    // Ignore storage failures.
  }

  const checkServerVersion = () => {
    fetch(`/play/index.html?t=${Date.now()}`, { cache: 'reload' })
      .then(response => response.text())
      .then(html => {
        const match = html.match(/<meta\s+name="app-version"\s+content="([^"]+)"/);
        const serverVersion = match ? match[1] : null;
        if (serverVersion && serverVersion !== APP_VERSION) {
          forceReload(serverVersion);
        }
      })
      .catch(() => {
        // Network/version checks are best effort.
      });
  };

  checkServerVersion();
  window.addEventListener('pageshow', event => {
    if (event.persisted) checkServerVersion();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkServerVersion();
  });
})();

const root = document.getElementById('root');
if (!root) {
  document.body.innerHTML = '<div style="background:#071a0e;color:white;padding:40px;font-family:sans-serif;">ERROR: Root element not found</div>';
  throw new Error('Root element not found');
}

try {
  createRoot(root).render(
    <StrictMode>
      <ErrorBoundary>
        <SettingsProvider>
          <App />
        </SettingsProvider>
      </ErrorBoundary>
    </StrictMode>,
  );
} catch (error) {
  console.error('[Sbarazzino] Render failed:', error);
  document.body.innerHTML = `<div style="background:#071a0e;color:white;padding:40px;font-family:sans-serif;">RENDER ERROR: ${String(error)}</div>`;
}

// The service worker is deliberately scoped to the game. Editorial pages stay
// outside its control and are served as ordinary static documents.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/play/', updateViaCache: 'none' })
      .then(registration => {
        console.log('[Sbarazzino] ServiceWorker registered:', registration.scope);

        registration.onupdatefound = () => {
          const installingWorker = registration.installing;
          if (!installingWorker) return;

          installingWorker.onstatechange = () => {
            if (installingWorker.state === 'installed' && navigator.serviceWorker.controller) {
              window.location.reload();
            }
          };
        };

        registration.update().catch(() => {});
      })
      .catch(error => {
        console.error('[Sbarazzino] ServiceWorker registration failed:', error);
      });
  });
}
